import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  MenuItem,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";

const IVA_OPTIONS = [
  { value: 0, label: "0%" },
  { value: 5, label: "5%" },
  { value: 15, label: "15%" },
  { value: 999, label: "No objeto de IVA" },
];

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (let c of cookies) {
    const t = c.trim();
    if (t.startsWith(name + "=")) return decodeURIComponent(t.substring(name.length + 1));
  }
  return null;
}

const Services = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [pvp1, setPvp1] = useState("");
  const [ivaPorcentaje, setIvaPorcentaje] = useState(15);
  const [estado, setEstado] = useState(true);
  const [description, setDescription] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "error",
    message: "",
  });

  useEffect(() => {
    const fetchCode = async () => {
      const r = await fetch("/servicios/next-code/", { credentials: "include" });
      const d = await r.json();
      if (d.next_code) setCode(d.next_code);
    };
    fetchCode();
  }, []);

  const ivaValor = useMemo(() => {
    const base = parseFloat(pvp1 || 0);
    if (ivaPorcentaje === 999) return "0.00";
    return (base * (ivaPorcentaje / 100)).toFixed(2);
  }, [pvp1, ivaPorcentaje]);

  const precioFinal = useMemo(() => {
    return (parseFloat(pvp1 || 0) + parseFloat(ivaValor || 0)).toFixed(2);
  }, [pvp1, ivaValor]);

  const handleLogout = async () => {
    await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    localStorage.clear();
    navigate("/login");
  };

  const handleSelect = (component) => {
    if (component === "AdminSection") navigate("/admin-panel/secciones");
    else if (component === "ModalInventario") setSubmenuOpen((s) => !s);
    else if (component === "AdminProduct") navigate("/admin-panel/inventario/productos");
    else if (component === "AdminVariant") navigate("/admin-panel/inventario/variantes");
    else if (component === "AdminProforma") navigate("/admin-panel/proformas");
    else if (component === "AdminClient") navigate("/admin-panel/clientes");
    else if (component === "AdminServices") navigate("/admin-panel/servicios");
    else if (component === "AdminPromotion") navigate("/admin-panel/promociones");
    else navigate("/admin-panel");
  };

  const handleSubmit = async () => {
    const csrf = getCSRFToken();
    const formData = new FormData();

    formData.append("code", code);
    formData.append("name", name);
    formData.append("pvp1", pvp1);
    formData.append("iva_porcentaje", ivaPorcentaje);
    formData.append("estado", estado ? "activo" : "inactivo");
    formData.append("description", description);

    try {
      const r = await fetch("/servicios/crear-servicio/", {
        method: "POST",
        credentials: "include",
        headers: csrf ? { "X-CSRFToken": csrf } : undefined,
        body: formData,
      });

      if (r.ok) {
        navigate("/admin-panel/servicios", {
          state: {
            snackbar: {
              type: "success",
              message: "Servicio creado exitosamente",
            },
          },
          replace: true,
        });
      } else {
        setSnackbar({
          open: true,
          type: "error",
          message: "Error al crear el servicio",
        });
      }
    } catch {
      setSnackbar({
        open: true,
        type: "error",
        message: "Error de conexión con el servidor",
      });
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#edf0f7ff" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        handleSelect={handleSelect}
        handleLogout={handleLogout}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            sx={{
              textTransform: "none",
              bgcolor: "#1565C0",
              "&:hover": { bgcolor: "#0D47A1" },
              mb: 3,
            }}
            onClick={() => navigate(-1)}
          >
            Regresar
          </Button>

          <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: "#1565C0" }}>
              Información del Servicio
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: "bold" }}>
              Los campos con (*) son obligatorios
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Nombre del Servicio"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Código"
                  value={code}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={estado}
                      onChange={(e) => setEstado(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={estado ? "Activo" : "Inactivo"}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="PVP"
                  value={pvp1}
                  onChange={(e) => setPvp1(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="IVA %"
                  value={ivaPorcentaje}
                  onChange={(e) => setIvaPorcentaje(parseInt(e.target.value, 10))}
                >
                  {IVA_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField fullWidth label="IVA $" value={ivaValor} InputProps={{ readOnly: true }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Precio Final"
                  value={precioFinal}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={6}
                  label="Descripción"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} textAlign="center">
                <Button
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    bgcolor: "#1565C0",
                    "&:hover": { bgcolor: "#0D47A1" },
                    mt: 2,
                  }}
                  onClick={handleSubmit}
                >
                  Crear Servicio
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.type}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Services;
